import { useEffect, useCallback, useRef } from 'react';
import { SimulationConfig, Ball } from '@/types';
import { STORAGE_KEY } from '@/constants/constants';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';

const STRIDE = 12;

export const usePhysicsWorker = (
  config: SimulationConfig,
  ballsRef: React.MutableRefObject<Ball[]>,
  workerRef: React.MutableRefObject<Worker | null>,
  onCollision?: (payload: any) => void
) => {
  const buffersRef = useRef<Float32Array[]>([]);
  const sabRef = useRef<SharedArrayBuffer | null>(null);

  useEffect(() => {
    workerRef.current = new Worker(new URL('../physics.worker.ts', import.meta.url), { type: 'module' });
    
    // Check for SharedArrayBuffer availability. 
    // This requires cross-origin isolation (COOP/COEP headers).
    const isSABAvailable = typeof SharedArrayBuffer !== 'undefined';
    
    const maxBalls = config.maxBalls || 2000;
    const bufferSize = maxBalls * STRIDE;

    if (isSABAvailable) {
        try {
            sabRef.current = new SharedArrayBuffer(bufferSize * 4);
            buffersRef.current = [new Float32Array(sabRef.current)];
        } catch (e) {
            console.warn("SharedArrayBuffer allocation failed, falling back to Transferables", e);
            sabRef.current = null;
        }
    }

    if (!sabRef.current) {
        // Fallback to Double-Buffering with Transferables
        buffersRef.current = [
            new Float32Array(bufferSize),
            new Float32Array(bufferSize)
        ];
    }

    // Attach a helper to the worker for the simulation loop to use
    // This avoids needing to change CoreSimulationLogic dramatically
    const worker = workerRef.current as any;
    worker.nextBuffer = null;
    worker.isUsingSAB = !!sabRef.current;
    
    // Seed the secondary buffer for the first STEP if not using SharedArrayBuffer
    if (!worker.isUsingSAB) {
        worker.nextBuffer = buffersRef.current[1];
    }

    workerRef.current.onmessage = (e) => {
        if (e.data.type === 'UPDATE') {
            const { data, count } = e.data;
            PerformanceTelemetry.recordWorkerReceived();
            
            // If using transferables (not SAB), 'data' is the returned buffer
            if (!worker.isUsingSAB && data) {
                worker.nextBuffer = data;
            }

            // Sync visual objects from binary data
            // We use the 'data' passed in the message which is either the shared view or the transferred buffer
            for (let i = 0; i < count; i++) {
                if (ballsRef.current[i]) {
                    ballsRef.current[i].pos.x = data[i * STRIDE + 0];
                    ballsRef.current[i].pos.y = data[i * STRIDE + 1];
                    ballsRef.current[i].vel.x = data[i * STRIDE + 2];
                    ballsRef.current[i].vel.y = data[i * STRIDE + 3];
                    ballsRef.current[i].radius = data[i * STRIDE + 4];
                    ballsRef.current[i].mass = data[i * STRIDE + 5];
                    ballsRef.current[i].angle = data[i * STRIDE + 9];
                }
            }
        } else if (e.data.type === 'COLLISION') {
            onCollision?.(e.data.payload);
        }
    };
    
    // Initialization message
    workerRef.current.postMessage({ 
        type: 'INIT', 
        payload: { 
            config: config, 
            width: window.innerWidth, 
            height: window.innerHeight,
            maxBalls: maxBalls,
            rngSeed: config.rngSeed
        },
        // If not using SAB, we send the first buffer to the worker
        buffer: !worker.isUsingSAB ? buffersRef.current[0] : buffersRef.current[0]
    }, !worker.isUsingSAB ? [buffersRef.current[0].buffer] : []);

    // If using transferables, we gave away buffer 0.
    // The next STEP from CoreSimulationLogic will trigger and we need to make sure 
    // it eventually gets buffer 1 or waits for buffer 0 to return.

    return () => workerRef.current?.terminate();
  }, [onCollision]); // Note: config and maxBalls are purposely omitted to avoid re-init

  useEffect(() => {
      if (workerRef.current && ballsRef.current.length > 0) {
          // Only send balls if the worker doesn't have them yet or if they were reset
          // For simplicity, we can send them when the length changes significantly or on init
          workerRef.current.postMessage({ 
              type: 'ADD_BALLS', 
              payload: { balls: ballsRef.current } 
          });
      }
  }, [config.mode]); // Re-send when mode changes or on first load

  useEffect(() => {
    if (workerRef.current) {
        workerRef.current.postMessage({ type: 'UPDATE_CONFIG', payload: config });
    }
  }, [config]);

  const resetWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'CLEAR_BALLS' });
    }
  }, [workerRef]);

  const sendBallsToWorker = useCallback((balls: Ball[], now?: number) => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls, now } });
    }
  }, [workerRef]);

  return { resetWorker, sendBallsToWorker };
};
